// src/components/education/periodic-table/MaterialDetail.tsx
// A component that displays detailed information about a selected material
import React from 'react';
import styled from 'styled-components';

export interface MaterialDetailProps {
  title: string;
  description: string;
  keyComponents: { element: string; description: string }[];
  applications: string[];
  onClose: () => void;
}

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const DetailContent = styled.div`
  width: 800px;
  background-color: rgba(149, 117, 205, 0.7);
  border-radius: 8px;
  padding: 35px;
  color: white;
  position: relative;
  border: 1px solid rgba(255, 165, 0, 0.3);
  box-shadow: 0 0 20px rgba(255, 165, 0, 0.2);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  color: #FFA500;
  font-size: 22px;
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  
  &:hover {
    background-color: rgba(255, 165, 0, 0.1);
  }
`;

const Title = styled.h2`
  color: #FFA500;
  margin-bottom: 20px;
  border-bottom: 1px solid rgba(255, 165, 0, 0.3);
  padding-bottom: 15px;
  font-size: 1.8rem;
`;

const Description = styled.p`
  margin-bottom: 30px;
  line-height: 1.8;
  font-size: 1.1rem;
`;

const SectionTitle = styled.h3`
  color: #FFA500;
  margin-top: 25px;
  margin-bottom: 20px;
  font-size: 1.5rem;
`;

const ComponentsList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin-bottom: 25px;
`;

const ComponentItem = styled.li`
  margin-bottom: 15px;
  display: flex;
  align-items: flex-start;
  font-size: 1.1rem;
  line-height: 1.5;
  
  &:before {
    content: "•";
    color: #FFA500;
    margin-right: 15px;
    font-size: 1.2rem;
  }
`;

const ElementSymbol = styled.span`
  font-weight: bold;
  color: #FFA500;
  margin-right: 5px;
`;

const ApplicationsList = styled.div`
  margin-top: 25px;
  padding: 20px;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 5px;
  border-left: 3px solid #FFA500;
  font-size: 1.1rem;
  line-height: 1.6;
`;

const MaterialDetail: React.FC<MaterialDetailProps> = ({
  title,
  description,
  keyComponents,
  applications,
  onClose
}) => {
  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Overlay onClick={onClose}>
      <DetailContent onClick={handleContentClick}>
        <CloseButton onClick={onClose}>✕</CloseButton>
        <Title>{title}</Title>
        <Description>{description}</Description>
        
        <SectionTitle>Key Chemical Components:</SectionTitle>
        <ComponentsList>
          {keyComponents.map((component, index) => (
            <ComponentItem key={index}>
              <ElementSymbol>{component.element}</ElementSymbol>: {component.description}
            </ComponentItem>
          ))}
        </ComponentsList>
        
        <SectionTitle>Real-World Applications:</SectionTitle>
        <ApplicationsList>
          {applications.join(', ')}
        </ApplicationsList>
      </DetailContent>
    </Overlay>
  );
};

export default MaterialDetail; 